/**
 * Where do the waypoints sit along the routed line?
 *
 * Needed whenever a new point has to be slotted into an existing route in the
 * right place. Appending is only ever correct when you're extending the end;
 * grab the line halfway round and the new point belongs between the waypoints
 * that bracket it, or the route drives out to it and back.
 *
 * The scan is forward-only and stateful rather than a nearest-point search per
 * waypoint. On a route that crosses itself — which scenic routes do constantly —
 * an independent search can match waypoint 8 to a stretch of road that belongs
 * to waypoint 3, and the resulting order is nonsense. Walking forward means the
 * results are monotonic by construction.
 */

import { bearingDegrees, cumulativeDistances, pointToSegmentMeters } from './geo.js';
import type { LngLat } from './types.js';

export interface WaypointLocation {
  /** Metres from the route's start. */
  alongMeters: number;
  /** Index of the geometry segment it matched. */
  segmentIndex: number;
  /** How far the waypoint sits from the line it matched, in metres. */
  distanceMeters: number;
  /** The point on the line it matched — the perpendicular foot, not a vertex. */
  point: LngLat;
}

export function locateWaypointsAlongRoute(
  coords: LngLat[],
  waypoints: Array<{ lng: number; lat: number }>,
): WaypointLocation[] {
  if (coords.length < 2 || waypoints.length === 0) return [];

  const cum = cumulativeDistances(coords);
  const out: WaypointLocation[] = [];
  let searchFrom = 0;

  waypoints.forEach((waypoint, waypointIndex) => {
    const p: LngLat = [waypoint.lng, waypoint.lat];

    // The last waypoint is the end of the line by definition; pinning it avoids
    // an out-and-back route matching it to the outbound leg.
    if (waypointIndex === waypoints.length - 1) {
      const lastSegment = coords.length - 2;
      const r = pointToSegmentMeters(p, coords[lastSegment]!, coords[lastSegment + 1]!);
      out.push({
        alongMeters: cum[cum.length - 1]!,
        segmentIndex: lastSegment,
        distanceMeters: r.distance,
        point: coords[coords.length - 1]!,
      });
      return;
    }

    let bestDistance = Infinity;
    let bestAlong = cum[searchFrom]!;
    let bestSegment = searchFrom;
    let bestPoint = coords[searchFrom]!;

    for (let i = searchFrom; i < coords.length - 1; i++) {
      const r = pointToSegmentMeters(p, coords[i]!, coords[i + 1]!);
      if (r.distance < bestDistance) {
        bestDistance = r.distance;
        bestSegment = i;
        bestAlong = cum[i]! + r.t * (cum[i + 1]! - cum[i]!);
        bestPoint = r.point;
      }
    }

    out.push({
      alongMeters: bestAlong,
      segmentIndex: bestSegment,
      distanceMeters: bestDistance,
      point: bestPoint,
    });
    searchFrom = bestSegment;
  });

  return out;
}

/**
 * Distance along the route of the point nearest to `target`.
 *
 * Deliberately separate from `locateWaypointsAlongRoute`, which pins its final
 * entry to the end of the line. Passing a single arbitrary point through that
 * function would hit the pinning branch and always report the route's full
 * length — which, when used to choose an insertion index, silently turns every
 * insert back into an append.
 */
export function distanceAlongRoute(coords: LngLat[], target: LngLat): WaypointLocation | null {
  if (coords.length < 2) return null;

  const cum = cumulativeDistances(coords);
  let bestDistance = Infinity;
  let bestAlong = 0;
  let bestSegment = 0;
  let bestPoint = coords[0]!;

  for (let i = 0; i < coords.length - 1; i++) {
    const r = pointToSegmentMeters(target, coords[i]!, coords[i + 1]!);
    if (r.distance < bestDistance) {
      bestDistance = r.distance;
      bestSegment = i;
      bestAlong = cum[i]! + r.t * (cum[i + 1]! - cum[i]!);
      bestPoint = r.point;
    }
  }

  return {
    alongMeters: bestAlong,
    segmentIndex: bestSegment,
    distanceMeters: bestDistance,
    point: bestPoint,
  };
}

/**
 * Distance along a line of the point nearest to `target`, looking first near
 * where it was last time.
 *
 * `distanceAlongRoute` takes the nearest point anywhere on the line, which is
 * wrong wherever the line passes close to itself — a road out and back, a
 * roundabout taken twice, a way back that loops round a junction. There the
 * nearest point can be the other stretch, and for one fix the car is placed a
 * kilometre and a half back and then forward again: the driven part of the
 * way back jumped with it. The route follower avoids exactly this by searching
 * a window around the last position; this is the same idea for any line.
 *
 * Searches from `behindMeters` before `nearAlong` to `aheadMeters` after it —
 * lopsided, since cars go forward — and accepts the best match there if it's
 * within `acceptMeters` of the line. Otherwise, or with no previous position,
 * the whole line: better to find the car somewhere than nowhere.
 */
export function distanceAlongRouteNear(
  coords: LngLat[],
  target: LngLat,
  nearAlong: number | null,
  { behindMeters = 250, aheadMeters = 1_500, acceptMeters = 100 } = {},
): WaypointLocation | null {
  if (coords.length < 2) return null;
  if (nearAlong === null || !Number.isFinite(nearAlong)) return distanceAlongRoute(coords, target);

  const cum = cumulativeDistances(coords);
  const from = nearAlong - behindMeters;
  const to = nearAlong + aheadMeters;
  let best: WaypointLocation | null = null;

  for (let i = 0; i < coords.length - 1; i++) {
    // Only segments that overlap the window.
    if (cum[i + 1]! < from) continue;
    if (cum[i]! > to) break;
    const r = pointToSegmentMeters(target, coords[i]!, coords[i + 1]!);
    if (!best || r.distance < best.distanceMeters) {
      best = {
        alongMeters: cum[i]! + r.t * (cum[i + 1]! - cum[i]!),
        segmentIndex: i,
        distanceMeters: r.distance,
        point: r.point,
      };
    }
  }

  return best && best.distanceMeters <= acceptMeters ? best : distanceAlongRoute(coords, target);
}

/**
 * Where a series of points fall along the line, in the order given.
 *
 * Forward-only, like `locateWaypointsAlongRoute`, and for the same reason: a
 * scenic route crosses itself, and an independent nearest-point search per
 * turn would place a manoeuvre from halfway round onto a stretch near the
 * start. Unlike that function it does not pin the last point to the end of the
 * route — these are turns, not destinations.
 */
export function locatePointsAlongRoute(coords: LngLat[], points: LngLat[]): number[] {
  if (coords.length < 2 || points.length === 0) return [];

  const cum = cumulativeDistances(coords);
  const out: number[] = [];
  let searchFrom = 0;

  for (const point of points) {
    let bestDistance = Infinity;
    let bestAlong = cum[searchFrom]!;
    let bestSegment = searchFrom;

    for (let i = searchFrom; i < coords.length - 1; i++) {
      const r = pointToSegmentMeters(point, coords[i]!, coords[i + 1]!);
      if (r.distance < bestDistance) {
        bestDistance = r.distance;
        bestSegment = i;
        bestAlong = cum[i]! + r.t * (cum[i + 1]! - cum[i]!);
        // A manoeuvre sits exactly on a vertex, so an effectively-zero distance
        // is the answer — stop rather than scanning the rest of the route.
        if (r.distance < 0.5) break;
      }
    }

    out.push(bestAlong);
    searchFrom = bestSegment;
  }

  return out;
}

/**
 * The array index a point at `alongMeters` should be spliced into.
 *
 * Returns `waypoints.length` when the point is past the last waypoint, which is
 * the append case.
 */
export function insertionIndexFor(locations: WaypointLocation[], alongMeters: number): number {
  for (let i = 0; i < locations.length; i++) {
    if (locations[i]!.alongMeters > alongMeters) return i;
  }
  return locations.length;
}

/**
 * Which way the road points at a given distance along the route.
 *
 * Used to orient the driving camera. Taken from the route rather than from the
 * GPS course on purpose: a course derived from consecutive fixes jitters badly
 * at low speed and is meaningless when stationary, which would leave the map
 * slowly spinning at every set of traffic lights. The road's own direction is
 * stable and is what the driver is about to follow anyway.
 *
 * Measured over a lookahead rather than the containing segment, because road
 * geometry contains very short segments whose individual bearings swing wildly.
 * The right lookahead depends on what's being oriented — see
 * CAMERA_LOOKAHEAD_METERS and VEHICLE_LOOKAHEAD_METERS.
 */
export function routeBearingAt(
  coords: LngLat[],
  alongMeters: number,
  lookaheadMeters = 60,
): number | null {
  if (coords.length < 2) return null;

  const cum = cumulativeDistances(coords);
  const total = cum[cum.length - 1]!;
  if (total <= 0) return null;

  const from = Math.max(0, Math.min(alongMeters, total - 1));
  const to = Math.min(total, from + lookaheadMeters);

  const pointAt = (metres: number): LngLat => {
    let i = 0;
    while (i < coords.length - 2 && cum[i + 1]! < metres) i++;
    const span = Math.max(1e-9, cum[i + 1]! - cum[i]!);
    const t = (metres - cum[i]!) / span;
    return [
      coords[i]![0] + t * (coords[i + 1]![0] - coords[i]![0]),
      coords[i]![1] + t * (coords[i + 1]![1] - coords[i]![1]),
    ];
  };

  const a = pointAt(from);
  const b = pointAt(to);
  if (a[0] === b[0] && a[1] === b[1]) return null;
  return bearingDegrees(a, b);
}

/**
 * The part of a path still ahead of you, starting from where you are now.
 *
 * A path back to the route is computed every twenty seconds or so — often
 * enough, given each computation is a routing request and a redraw in front of
 * a driver. But a car covers a few hundred metres in that time, so the drawn
 * line would otherwise visibly begin somewhere the car no longer is, which
 * reads as a bug whether or not it is one.
 *
 * Trimming what's already been covered and starting the line at the current
 * position fixes both cases: follow the path and it shortens ahead of you;
 * ignore it and you get a short straight hint back onto it.
 */
/**
 * How far from the line the drawn path may reach back to the car.
 *
 * A short hop from the car to the road is helpful: it says "this line is
 * yours". A long one is a lie about where you can drive — it is a straight
 * chord over whatever lies between, which is how a guide line ends up drawn
 * across a field to a main road. Past this, the line starts on the road and
 * the driver can see for themselves that they are not on it yet.
 */
const MAX_CONNECTOR_METERS = 60;

export function remainingPath(
  path: LngLat[],
  position: LngLat,
  maxConnectorMeters = MAX_CONNECTOR_METERS,
): LngLat[] {
  if (path.length === 0) return [];
  if (path.length === 1) return [position, ...path];

  const located = distanceAlongRoute(path, position);
  if (!located) return [position, ...path];

  const connected = located.distanceMeters <= maxConnectorMeters;

  const rest = path.slice(located.segmentIndex + 1);
  if (rest.length === 0) {
    return connected ? [position, path[path.length - 1]!] : [located.point, path[path.length - 1]!];
  }

  /*
    Start at the point on the line level with the driver, not at the next
    vertex.

    Dropping the whole current segment threw away everything between the car and
    that vertex — on a motorway leg, hundreds of metres of it. The line visibly
    detached from the car, then jumped forward a chunk at a time as each vertex
    was passed, instead of shortening smoothly. Including the perpendicular foot
    also keeps the drawn line on the road rather than cutting the corner from
    wherever the car happens to be sitting.
  */
  const trimmed = dedupe(connected ? [position, located.point, ...rest] : [located.point, ...rest]);
  // Sitting on the final vertex collapses everything to one point, which is not
  // a line. Fall back to the degenerate two-point form rather than something
  // undrawable.
  return trimmed.length >= 2 ? trimmed : [position, path[path.length - 1]!];
}

/**
 * Drops points that repeat the one before them.
 *
 * Only matters at the joins: a driver sitting exactly on a vertex makes the
 * position, the perpendicular foot and the next vertex all the same place.
 */
function dedupe(points: LngLat[]): LngLat[] {
  const out: LngLat[] = [];
  for (const point of points) {
    const previous = out[out.length - 1];
    if (previous && Math.abs(point[0] - previous[0]) < 1e-7 && Math.abs(point[1] - previous[1]) < 1e-7) {
      continue;
    }
    out.push(point);
  }
  return out;
}

/**
 * Lookahead for orienting the map.
 *
 * Long enough to smooth through a bend, so the view doesn't swing about every
 * time the road wiggles.
 */
export const CAMERA_LOOKAHEAD_METERS = 60;

/**
 * Lookahead for orienting the vehicle marker.
 *
 * Short, because the car has to sit square on the road directly beneath it. Use
 * the camera's smoothed bearing here and on any bend the car visibly points off
 * the road it is on — the map has been rotated to the average direction while
 * the car is drawn along it too, so the difference between them goes unshown.
 */
export const VEHICLE_LOOKAHEAD_METERS = 18;

/**
 * The line from where you are to where you would join the guide, when the two
 * are far enough apart to have been left unjoined.
 *
 * `remainingPath` starts the guide on the road rather than at the car once the
 * car is more than a connector's length away, so that the guide is never drawn
 * cutting across open ground. That is right for the guide, but it leaves a
 * visible gap, and a driver whose house sits back from the road is left
 * floating in a field with the line starting somewhere off to the side.
 *
 * This is that gap, handed back separately so it can be drawn as what it is:
 * not a road, and not part of the route. Null when there is no gap, which is
 * the same threshold `remainingPath` uses — the two agree by construction.
 */
export function connectorPath(
  path: LngLat[],
  position: LngLat,
  maxConnectorMeters = MAX_CONNECTOR_METERS,
): LngLat[] | null {
  if (path.length < 2) return null;
  const located = distanceAlongRoute(path, position);
  if (!located) return null;
  if (located.distanceMeters <= maxConnectorMeters) return null;
  return [position, located.point];
}
